import { useRef, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import classes from "./PhoneInputs.module.css";
import { SearchContext } from '../../../App.js';
import { appendHomeSearchParams } from '../../../util/activitySearchOptions';
import PhoneOptions from './PhoneOptions.jsx';

export const PhoneInputs = () => {  
  const [localActiveInput, setLocalActiveInput] = useState(null);
  const searchContainerRef = useRef(null);  
  const location = useLocation();

  const params = new URLSearchParams(location?.search);

  const { 
    searchFields, 
    setSearchFields, 
    setActiveInput,  
    setIsPhoneSearch,
    updateSearchField 
  } = useContext(SearchContext); 

  useEffect(() => {
    setIsPhoneSearch(true);
    return () => setIsPhoneSearch(false);
  }, [setIsPhoneSearch]);

  useEffect(() => {
    setSearchFields({
      activity: params?.get('activity') || '',
      location: params?.get('location') || '',
      locationPlaceId: params?.get('locationPlaceId') || ''
    });    
    if (params?.get('activity') || params?.get('location')) {
      setIsButtonEnabled(true);
    } else {
      setIsButtonEnabled(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRefs.activity.current) {
        inputRefs.activity.current.focus();
        setLocalActiveInput('activity');
        setActiveInput('activity');
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [setActiveInput]);

  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setActiveInput(null);
        setLocalActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setActiveInput]);

  const inputRefs = {
    activity: useRef(null),
    location: useRef(null),
  };

  const handleClick = (input) => {
    if (localActiveInput === input) return;
    inputRefs[input].current.focus();
    setLocalActiveInput(input);
    setActiveInput(input);
  };

  const handleChange = (input, event) => {
    const value = event.target.value;
    setSearchFields((prevFields) => ({
      ...prevFields,
      [input]: value
    }));
    updateSearchField(input, value);
    if (value !== "") {
      setIsButtonEnabled(true);
    }
  };

  const handleSearchResults = (e) => {
    e.preventDefault();
    const queryParams = appendHomeSearchParams(new URLSearchParams(), {
      activity: searchFields.activity,
      location: searchFields.location,
      locationPlaceId: searchFields.locationPlaceId,
    });
    window.location.href = `/search?${queryParams.toString()}`;
  };

  return (
    <>
      <form onSubmit={handleSearchResults} className={classes.searchContainer} ref={searchContainerRef}>
        <div
          className={`${classes.inputGroup} ${localActiveInput === 'activity' ? classes.inputGroupActive : ''}`}
          onClick={() => handleClick('activity')}
        >
          <div className={classes.searchIconContainer} style={{ margin: "1.05rem .687rem .85rem 1.235rem" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="19.5" height="19.5" viewBox="0 0 19.5 19.5" fill="none">
              <path d="M14.5163 12.9148C15.5278 11.5786 16.0887 9.93102 16.0887 8.24324C16.0887 3.98358 12.6534 0.517578 8.43693 0.517578C4.22044 0.517578 0.785156 3.98358 0.785156 8.24324C0.785156 12.5029 4.21043 15.9588 8.43693 15.9588C10.1095 15.9588 11.742 15.4063 13.0741 14.3816L18.853 20.0176L20.2852 18.5408L14.5163 12.9148ZM8.6773 13.8993H8.43693C5.34217 13.8993 2.8283 11.3576 2.8283 8.23319C2.8283 5.10877 5.34217 2.58713 8.43693 2.58713C11.5317 2.58713 14.0355 5.11881 14.0355 8.24324C14.0355 11.3677 11.6819 13.7687 8.6773 13.8993Z" fill="#77808A" />           
            </svg>
          </div>
          <div className={classes.searchText}>
            <input
              autoFocus
              className='regularLarge'
              placeholder='Studio suchen'
              type="text"
              ref={inputRefs.activity}
              onChange={(e) => handleChange('activity', e)}
              value={searchFields.activity}
            />
          </div>
        </div>
        <div
          className={`${classes.inputGroup} ${localActiveInput === 'location' ? classes.inputGroupActive : ''}`}
          onClick={() => handleClick('location')}
        >
          <div className={classes.activityIconContainer} style={{ margin: "1rem .687rem .9rem 1.235rem" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="21" viewBox="0 0 15 21" fill="none">
              <path d="M7.58839 21.0569L7.7265 21.1465H7.95339L8.12109 20.9474C8.2 20.8677 8.26906 20.7881 8.31838 20.7184C9.26539 19.6431 10.1828 18.4484 11.2877 16.8355C12.2544 15.4615 13.0534 14.1473 13.744 12.8132C14.3063 11.6981 14.6713 10.812 14.9376 9.92591C15.0954 9.39823 15.2039 8.90042 15.2631 8.39266C15.3125 7.94463 15.3026 7.52648 15.2927 7.14814C15.2237 6.47113 15.0856 5.84389 14.8784 5.23656H14.908L14.76 4.89805C13.8821 2.76744 12.353 1.31384 10.2321 0.557175C9.75863 0.377965 9.2358 0.268447 8.64391 0.198754C8.07176 0.129061 7.56866 0.129061 7.00637 0.198754C6.40463 0.268447 5.8818 0.377965 5.40829 0.557175C3.28738 1.31384 1.75835 2.76744 0.880391 4.89805C0.604179 5.57507 0.426615 6.31183 0.308238 7.16806C0.298374 7.49661 0.288509 7.94463 0.347697 8.39266C0.406885 8.90042 0.505532 9.40819 0.663368 9.92591C0.929715 10.802 1.29471 11.6981 1.86686 12.8132C2.54753 14.1473 3.35643 15.4715 4.31331 16.8355C5.41816 18.4484 6.33557 19.6431 7.27272 20.6985M13.5269 8.93029C13.3395 9.71683 13.0337 10.4635 12.7674 11.0908C12.2347 12.2656 11.5441 13.4803 10.6662 14.8243C9.91646 15.9792 9.0385 17.1839 8.00271 18.5081C7.94352 18.5878 7.87447 18.6674 7.81528 18.7471C7.74623 18.6674 7.67717 18.5878 7.61799 18.5081C6.56246 17.164 5.72396 15.9892 4.95451 14.8243C4.07656 13.4803 3.38603 12.2556 2.8632 11.0908C2.58699 10.4635 2.28118 9.70687 2.09375 8.93029C1.97537 8.3827 1.92605 7.8849 1.95564 7.39705C2.06416 5.25648 3.25779 3.39468 5.13208 2.42893C5.75356 2.11033 6.37503 1.92117 7.03597 1.83156C7.29245 1.80169 7.54893 1.79174 7.81528 1.79174C8.08163 1.79174 8.33811 1.80169 8.58473 1.83156C9.24566 1.92117 9.86714 2.11033 10.4886 2.42893C12.3629 3.39468 13.5565 5.25648 13.6651 7.39705C13.6946 7.8849 13.6453 8.3827 13.5269 8.93029Z" fill="#77808A" />
              <path d="M7.82514 4.3206C6.07909 4.3206 4.7079 5.7045 4.7079 7.45678C4.7079 9.20907 6.07909 10.583 7.82514 10.6029C9.5712 10.583 10.9325 9.20907 10.9325 7.45678C10.9325 5.7045 9.56133 4.3206 7.82514 4.3206ZM7.84487 8.96016C7.03597 8.95021 6.3553 8.28314 6.3553 7.45678C6.3553 6.63042 6.96692 5.99323 7.81528 5.97332C8.65378 5.99323 9.27526 6.62047 9.29498 7.45678C9.29498 8.28314 8.65378 8.95021 7.84487 8.96016Z" fill="#77808A" />          
            </svg>
          </div>
          <div className={classes.searchText}>
            <input
              className='regularLarge'
              type="text"
              placeholder="Standort"
              ref={inputRefs.location}
              value={searchFields.location}
              onChange={(e) => handleChange('location', e)}
            />
          </div>
        </div>
        {localActiveInput == null && (
          <button
            className={`${classes.formButton} regularLarge`}
            style={{
              backgroundColor: isButtonEnabled ? 'black' : '',
              color: isButtonEnabled ? 'white' : ''
            }}
            disabled={!isButtonEnabled}
            type='submit'
          >
            Suchen
          </button>
        )}
        {localActiveInput != null && (
          <PhoneOptions
            localActiveInput={localActiveInput} 
            setLocalActiveInput={setLocalActiveInput} 
            updateSearchField={updateSearchField} 
            setIsButtonEnabled={setIsButtonEnabled}
          />
        )}
      </form>
    </>
  );
};

export default PhoneInputs;
